import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { createCompany, getCompanyDetail, uploadCompanyLogo } from '../api/companyApi'
import { submitMasterChange } from '../../../approval/masters/api/masterApprovalApi'
import { appToast } from '../../../../components/AppToast'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000'

function normalizeText(value) {
  const trimmed = String(value || '').trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function toAssetUrl(path) {
  if (!path) return ''
  if (path.startsWith('http://') || path.startsWith('https://')) return path
  if (path.startsWith('/')) return `${API_BASE_URL}${path}`
  return `${API_BASE_URL}/${path}`
}

export function useCompanyEntryScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editingCompanyId = searchParams.get('id')
  const returnTo = searchParams.get('returnTo') || '/masters/company/list'

  const savingRef = useRef(false)

  const [form, setForm] = useState({
    name: '',
    gst_tax_number: '',
    company_website: '',
    support_phone: '',
    corporate_email: '',
    fax_number: '',
    currency: 'INR',
    is_archive: 0,
    company_logo_path: '',
    weekend_days: ['Sunday'],
    holidays: [{ id: 'init-1', name: '', from_date: '', to_date: '' }],
  })
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('')

  const [isLoading, setIsLoading] = useState(Boolean(editingCompanyId))
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [pendingApproval, setPendingApproval] = useState(null)

  useEffect(() => {
    let active = true

    async function loadIfEditing() {
      if (!editingCompanyId) return
      setIsLoading(true)
      setError('')

      try {
        const detail = await getCompanyDetail(editingCompanyId)
        if (!active) return

        let parsedWeekendDays = ['Sunday']
        if (detail?.weekend_days) {
          parsedWeekendDays = detail.weekend_days.split(',').map(d => d.trim()).filter(Boolean)
        } else if (detail?.weekendDays) {
          parsedWeekendDays = detail.weekendDays.split(',').map(d => d.trim()).filter(Boolean)
        }

        let parsedHolidays = []
        if (Array.isArray(detail?.holidays) && detail.holidays.length > 0) {
          parsedHolidays = detail.holidays.map(h => {
            const name = h.name || ''
            const hDate = h.hDate || h.h_date || ''
            const dates = hDate.split(',').map(d => d.trim()).filter(Boolean).sort()
            let from_date = ''
            let to_date = ''
            if (dates.length > 1) {
              from_date = dates[0]
              to_date = dates[dates.length - 1]
            } else if (dates.length === 1) {
              from_date = dates[0]
            }
            return {
              id: h.id || `temp-${Date.now()}-${Math.random()}`,
              name,
              from_date,
              to_date
            }
          })
        } else {
          parsedHolidays = [{ id: `init-${Date.now()}`, name: '', from_date: '', to_date: '' }]
        }

        setForm({
          name: detail?.name || '',
          gst_tax_number: detail?.gst_tax_number || '',
          company_website: detail?.company_website || '',
          support_phone: detail?.support_phone || '',
          corporate_email: detail?.corporate_email || '',
          fax_number: detail?.fax_number || '',
          currency: detail?.currency || 'INR',
          is_archive: Number(detail?.is_archive || 0),
          company_logo_path: detail?.company_logo_path || '',
          weekend_days: parsedWeekendDays,
          holidays: parsedHolidays,
        })
        setLogoPreviewUrl(toAssetUrl(detail?.company_logo_path || ''))
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load company details.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadIfEditing()

    return () => {
      active = false
    }
  }, [editingCompanyId])

  useEffect(() => {
    return () => {
      if (logoPreviewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreviewUrl)
      }
    }
  }, [logoPreviewUrl])

  const completionPercent = useMemo(() => {
    const trackedFields = ['name', 'gst_tax_number', 'company_website', 'support_phone', 'corporate_email', 'fax_number']
    const filledCount = trackedFields.filter((key) => String(form[key] || '').trim().length > 0).length
    return Math.round((filledCount / trackedFields.length) * 100)
  }, [form])

  function patchForm(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function toggleWeekendDay(day) {
    setForm((prev) => {
      const current = prev.weekend_days || []
      const updated = current.includes(day)
        ? current.filter((d) => d !== day)
        : [...current, day]
      return { ...prev, weekend_days: updated }
    })
  }

  function addHoliday() {
    setForm((prev) => {
      const current = prev.holidays || []
      return {
        ...prev,
        holidays: [
          ...current,
          { id: `new-${Date.now()}-${Math.random()}`, name: '', from_date: '', to_date: '' },
        ],
      }
    })
  }

  function removeHoliday(id) {
    setForm((prev) => {
      const current = prev.holidays || []
      const filtered = current.filter((h) => h.id !== id)
      const updated = filtered.length > 0 ? filtered : [{ id: `new-${Date.now()}-${Math.random()}`, name: '', from_date: '', to_date: '' }]
      return {
        ...prev,
        holidays: updated,
      }
    })
  }

  function updateHoliday(id, field, value) {
    setForm((prev) => {
      const current = prev.holidays || []
      const updated = current.map((h) => (h.id === id ? { ...h, [field]: value } : h))
      return { ...prev, holidays: updated }
    })
  }

  function buildPayload() {
    const holidaysPayload = (form.holidays || [])
      .filter((h) => h.name.trim() || h.from_date || h.to_date)
      .map((h) => {
        let hDate = ''
        if (h.from_date && h.to_date && h.from_date !== h.to_date) {
          const start = new Date(h.from_date)
          const end = new Date(h.to_date)
          const dates = []
          const curr = new Date(start)
          if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && start <= end) {
            while (curr <= end) {
              dates.push(curr.toISOString().split('T')[0])
              curr.setDate(curr.getDate() + 1)
            }
            hDate = dates.join(',')
          } else {
            hDate = h.from_date || h.to_date || ''
          }
        } else {
          hDate = h.from_date || h.to_date || ''
        }

        return {
          id: typeof h.id === 'string' && h.id.startsWith('new-') ? undefined : h.id,
          name: h.name,
          hDate,
        }
      })

    return {
      name: normalizeText(form.name),
      gst_tax_number: normalizeText(form.gst_tax_number),
      company_website: normalizeText(form.company_website),
      support_phone: normalizeText(form.support_phone),
      corporate_email: normalizeText(form.corporate_email),
      fax_number: normalizeText(form.fax_number),
      currency: normalizeText(form.currency) || 'INR',
      is_archive: Number(form.is_archive || 0),
      weekend_days: (form.weekend_days || []).join(','),
      holidays: holidaysPayload,
    }
  }

  function handleLogoFileChange(file) {
    if (!file) {
      setLogoFile(null)
      return
    }

    setLogoFile(file)

    if (logoPreviewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreviewUrl)
    }

    setLogoPreviewUrl(URL.createObjectURL(file))
  }

  async function uploadLogoIfNeeded(targetCompanyId) {
    if (!logoFile || !targetCompanyId) return

    const response = await uploadCompanyLogo(targetCompanyId, logoFile)
    const uploadedPath = response?.path || ''

    setForm((prev) => ({ ...prev, company_logo_path: uploadedPath }))
    setLogoFile(null)
    setLogoPreviewUrl(toAssetUrl(uploadedPath))
  }

  async function handleSave() {
    if (savingRef.current) return
    savingRef.current = true
    setError('')
    setSuccess('')
    setPendingApproval(null)

    if (!String(form.name || '').trim()) {
      setError('Company name is required.')
      savingRef.current = false
      return
    }

    const holidays = form.holidays || []
    for (const h of holidays) {
      const nameTrimmed = String(h.name || '').trim()
      const isBlank = !nameTrimmed && !h.from_date && !h.to_date
      if (isBlank) continue

      if (!nameTrimmed && (h.from_date || h.to_date)) {
        setError('Please enter a Holiday Name for the selected holiday dates.')
        savingRef.current = false
        return
      }
      if (nameTrimmed && !h.from_date) {
        setError(`Please specify From Date for holiday "${nameTrimmed}".`)
        savingRef.current = false
        return
      }
      if (h.to_date && h.from_date && h.from_date > h.to_date) {
        setError(`From Date cannot be after To Date for holiday "${nameTrimmed}".`)
        savingRef.current = false
        return
      }
    }

    setIsSaving(true)

    try {
      const payload = buildPayload()

      // Upload logo first if it's a new file on an existing company
      if (logoFile && editingCompanyId) {
        await uploadLogoIfNeeded(editingCompanyId)
      }

      const action = editingCompanyId ? 'UPDATE' : 'CREATE'
      const result = await submitMasterChange({
        module_key: 'company',
        action,
        entity_id: editingCompanyId ? Number(editingCompanyId) : undefined,
        payload,
      })

      const msg = result?.message || (editingCompanyId
        ? 'Company update submitted for approval.'
        : 'Company creation submitted for approval.')
      appToast.success(msg)
      setPendingApproval({
        requestId: result?.approval_request_id,
        message: msg,
      })
      setSuccess(msg)

      savingRef.current = false
      setIsSaving(false)
      navigate(returnTo || '/masters/company/list')
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to submit company change for approval.'))
      savingRef.current = false
      setIsSaving(false)
    }
  }

  return {
    state: {
      form,
      logoPreviewUrl,
      isLoading,
      isSaving,
      error,
      success,
      pendingApproval,
      completionPercent,
      isEditMode: Boolean(editingCompanyId),
      returnTo,
    },
    actions: {
      patchForm,
      handleSave,
      handleLogoFileChange,
      toggleWeekendDay,
      addHoliday,
      removeHoliday,
      updateHoliday,
    },
  }
}
